/**
 * Host-side guard for vendor `provide` (docs/vendor-apps-contract.md §4).
 *
 * A vendor tells us the name of the patient open in THEIR app; we compare it
 * to the chart open in OURS. If the two don't resemble each other we reject
 * the provide — buffer and link both — because the markdown is probably the
 * wrong patient's and the buffer feeds the note.
 *
 * "Resemble" is deliberately loose: it exists to catch Ann Kim vs Bob Smith,
 * not to adjudicate Kate vs Katherine. Rule: last names resemble AND first
 * names resemble, where two tokens resemble when they are equal, one is a
 * ≥3-char prefix of the other, or they're within a small edit distance.
 * Multi-token / hyphenated surnames match if ANY token pair resembles.
 * A lone initial matches a first name that starts with it.
 *
 * Known gap (v1): unrelated nicknames — Bob/Robert, Peggy/Margaret — fail.
 * The rail shows both names so the clinician sees why; a "link anyway"
 * override is v1.1.
 */

const strip = (s) =>
  String(s || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // diacritics
    .toLowerCase()
    .replace(/[^a-z\s-]/g, ' ')
    .replace(/[-\s]+/g, ' ')
    .trim();

/** Accepts "First Last", "Last, First", or { first, last }. */
export function parseName(name) {
  if (name && typeof name === 'object') {
    return { first: strip(name.first).split(' ').filter(Boolean), last: strip(name.last).split(' ').filter(Boolean) };
  }
  const raw = String(name || '');
  if (raw.includes(',')) {
    const [last, first] = raw.split(',');
    return { first: strip(first).split(' ').filter(Boolean), last: strip(last).split(' ').filter(Boolean) };
  }
  const tokens = strip(raw).split(' ').filter(Boolean);
  if (tokens.length === 0) return { first: [], last: [] };
  if (tokens.length === 1) return { first: [], last: tokens };
  return { first: tokens.slice(0, -1), last: tokens.slice(-1) };
}

function editDistance(a, b) {
  const m = a.length;
  const n = b.length;
  if (!m) return n;
  if (!n) return m;
  let prev = Array.from({ length: n + 1 }, (_, j) => j);
  for (let i = 1; i <= m; i++) {
    const cur = [i];
    for (let j = 1; j <= n; j++) {
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    prev = cur;
  }
  return prev[n];
}

/**
 * Initials and prefix truncation (Kath / Katherine) are first-name
 * phenomena; surnames get equality or edit distance only, so Kim / Kimball
 * and Smith / Smithson stay different people.
 */
export function tokensResemble(a, b, { allowPrefix = false } = {}) {
  if (!a || !b) return false;
  if (a === b) return true;
  const [short, long] = a.length <= b.length ? [a, b] : [b, a];
  if (allowPrefix) {
    if (short.length === 1) return long.startsWith(short); // initial
    if (short.length >= 3 && long.startsWith(short)) return true; // Jo / Joanna excluded
  }
  const allowed = long.length >= 7 ? 2 : long.length >= 4 ? 1 : 0;
  return editDistance(a, b) <= allowed;
}

const anyPair = (as, bs, opts) => as.some((a) => bs.some((b) => tokensResemble(a, b, opts)));

/**
 * @param {string|{first,last}} ours   name on the chart open in Comprehend
 * @param {string|{first,last}} theirs name the vendor says is open in their app
 * @returns {boolean}
 */
export function namesResemble(ours, theirs) {
  const o = parseName(ours);
  const t = parseName(theirs);
  if (!o.last.length || !t.last.length) return false;
  if (!anyPair(o.last, t.last)) return false;
  // A vendor that only has a surname can't be linked — we need some first-name signal.
  if (!o.first.length || !t.first.length) return false;
  return anyPair(o.first, t.first, { allowPrefix: true });
}

export default namesResemble;
